// @flow
import React, { Fragment, Component } from 'react';
import { Surface } from 'gl-react-dom';
import GLImage from 'gl-react-image';
import ReactResizeDetector from 'react-resize-detector';
import _format from 'date-fns/format';
import ReactGA from 'react-ga';

import LineShader, { type ShaderSettings } from 'components/shaders/LineShader';
import type { DroppedImage } from 'components/ImageDrop';
import type { Resolution } from 'types/Settings';

import { Wrap, SavingWrap, RenderWrap } from './BrandImageViewer.styled';

type Props = {
  image: DroppedImage,
  settings: ShaderSettings,
  resolution: Resolution,
};

type State = {
  elementSize: { width: number, height: number },
  width: number,
  height: number,
  isSaving: boolean,
  isPreparingRender: boolean,
  isSaving: boolean,
};

class BrandImageViewer extends Component<Props, State> {
  state = {
    elementSize: { width: 0, height: 0 },
    width: 0,
    height: 0,
    isPreparingRender: false,
    isRendering: false,
    isSaving: false,
  };

  componentDidUpdate(prevProps: Props) {
    const { isSaving, isPreparingRender, isRendering } = this.state;

    if (isSaving && !isPreparingRender) {
      this.prepareRenderImage();
    } else if (isSaving && isPreparingRender && !isRendering) {
      setTimeout(() => {
        this.renderImage();
      }, 1000);
    }

    const { elementSize } = this.state;
    const {
      image: { size: prevImageSize },
      resolution: prevResolution,
    } = prevProps;
    const {
      image: { size: imageSize },
      resolution,
    } = this.props;

    if (
      elementSize.width > 0 &&
      (resolution.width !== prevResolution.width ||
        resolution.height !== prevResolution.height)
    ) {
      const { width, height } = this.getCalculatedSizeForCanvas(
        elementSize.width,
        elementSize.height,
        resolution,
      );

      // We can set state because we have a condition
      this.setState({ width, height }); // eslint-disable-line
    }
  }

  componentDidMount() {
    document.addEventListener('keyup', this.handleEnterKeyUp);
  }

  componentWillUnmount() {
    document.removeEventListener('keyup', this.handleEnterKeyUp);
  }

  handleEnterKeyUp = ({ key }) => {
    if (key === 'Enter') {
      const { isSaving } = this.state;
      if (!isSaving) {
        this.saveImage();
      }
    }
  };

  getImageSize = (
    imgWidth: number,
    imgHeight: number,
    containerWidth: number,
    containerHeight: number,
  ) => {
    const aspectFitSize = { width: containerWidth, height: containerHeight };
    const mW = containerWidth / imgWidth;
    const mH = containerHeight / imgHeight;
    if (mH < mW) aspectFitSize.width = mH * imgWidth;
    else if (mW < mH) aspectFitSize.height = mW * imgHeight;
    return aspectFitSize;
  };

  onResize = (width: number, height: number) => {
    const { image, resolution } = this.props;
    if (image) {
      this.setState({
        elementSize: { width, height },
        ...this.getCalculatedSizeForCanvas(width, height, resolution),
      });
    }
  };

  getCalculatedSizeForCanvas = (
    width: number,
    height: number,
    resolution: Resolution,
  ) => {
    if (resolution) {
      const size = this.getImageSize(
        resolution.width,
        resolution.height,
        width,
        height,
      );

      return {
        width: size.width,
        height: size.height,
      };
    }

    return { width: 0, height: 0 };
  };

  calculateLabel = (value: number) => {
    if (value <= 20) {
      return 'Between 0 and 20';
    } else if (value <= 40) {
      return 'Between 21 and 40';
    } else if (value <= 60) {
      return 'Between 41 and 60';
    } else if (value <= 80) {
      return 'Between 61 and 80';
    }

    return 'Between 81 and 100';
  };

  calculateThicknessLabel = (value: number) => {
    if (value <= 10) {
      return 'Between 0 and 10';
    } else if (value <= 20) {
      return 'Between 11 and 20';
    } else if (value <= 30) {
      return 'Between 21 and 30';
    } else if (value <= 40) {
      return 'Between 31 and 40';
    }

    return 'Between 41 and 50';
  };

  saveImage = () => {
    ReactGA.event({
      category: 'Image',
      action: 'Save',
    });

    const { settings } = this.props;

    ReactGA.event({
      category: 'Settings',
      action: 'Contrast',
      label: this.calculateLabel(settings.contrast),
    });

    ReactGA.event({
      category: 'Settings',
      action: 'Brightness',
      label: this.calculateLabel(settings.brightness),
    });

    ReactGA.event({
      category: 'Settings',
      action: 'Thickness',
      label: this.calculateThicknessLabel(settings.thickness),
    });

    this.setState({ isSaving: true });
  };

  prepareRenderImage = () => {
    setTimeout(() => {
      this.setState({ isPreparingRender: true });
    }, 10);
  };

  renderImage = () => {
    this.setState({ isRendering: true }, () => {
      if (this.renderRef) {
        const {
          image: { name },
        } = this.props;

        const { canvas } = this.renderRef.glView;

        canvas.toBlob(blob => {
          const url = URL.createObjectURL(blob);
          const element = document.createElement('a');
          element.setAttribute('href', url);

          const date = _format(new Date(), 'DD-MM-YYYY-HH:mm');
          const fileName = `${name.substring(
            0,
            name.lastIndexOf('.'),
          )}-branded-${date}.png`;

          element.setAttribute('download', fileName);

          element.style.display = 'none';
          document.body.appendChild(element);

          element.click();
          document.body.removeChild(element);

          this.setState({
            isRendering: false,
            isSaving: false,
            isPreparingRender: false,
          });
        }, 'image/png');
      }
    });
  };

  renderRef = null;

  setRenderRef = element => {
    this.renderRef = element;
  };

  getCanvasSize = () => {
    const { width, height } = this.state;
    const { resolution } = this.props;

    let usedWidth = width;
    let usedHeight = height;

    if (resolution.width < width && resolution.height < height) {
      usedWidth = resolution.width;
      usedHeight = resolution.height;
    }

    return { width: usedWidth, height: usedHeight };
  };

  getRenderSettings = () => {
    const { settings, resolution } = this.props;
    const { width } = this.getCanvasSize();
    const newSettings = {
      ...settings,
      thickness: (settings.thickness / width) * resolution.width,
    };

    return newSettings;
  };

  render() {
    const { settings, image, resolution } = this.props;

    const { isSaving, isPreparingRender } = this.state;

    const { width, height } = this.getCanvasSize();
    return (
      <Fragment>
        <ReactResizeDetector
          refreshMode="throttle"
          refreshRate={200}
          handleWidth
          handleHeight
          onResize={this.onResize}
        >
          <Wrap>
            <Surface width={width} height={height}>
              <LineShader settings={settings}>
                <GLImage
                  source={image.croppedSrc || image.src}
                  resizeMode="cover"
                  center={[0.5, 0.5]}
                />
              </LineShader>
            </Surface>
          </Wrap>
          {isSaving && <SavingWrap>SAVING...</SavingWrap>}
        </ReactResizeDetector>
        {isPreparingRender && (
          <RenderWrap width={resolution.width} height={resolution.height}>
            <Surface
              ref={this.setRenderRef}
              width={resolution.width}
              height={resolution.height}
              webglContextAttributes={{ preserveDrawingBuffer: true }}
            >
              {isPreparingRender && (
                <LineShader settings={this.getRenderSettings()}>
                  <GLImage
                    source={image.croppedSrc || image.src}
                    resizeMode="cover"
                  />
                </LineShader>
              )}
            </Surface>
          </RenderWrap>
        )}
      </Fragment>
    );
  }
}

export default BrandImageViewer;



// WEBPACK FOOTER //
// /opt/build/repo/src/app/components/BrandImageViewer/BrandImageViewer.js